import React from 'react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, Award, History, Users, Quote } from 'lucide-react';
import SEO from '../components/SEO';
import { companyDetails } from '../lib/mock';

export default function About() {
  return (
    <div className="pt-20">
      <SEO 
        title="About Us - Water Treatment Plant Company Since 2000"
        description="BG TECHNO CHEM, established 2000 in Ludhiana, Punjab, is an ISO 9001 & 14001 certified water treatment plant manufacturer offering environmental consultancy and biotechnological STP, ETP & WTP solutions."
        keywords="water treatment company Ludhiana, water treatment plant manufacturer Punjab, ISO certified water treatment company, environmental consultancy Ludhiana, about BG TECHNO CHEM"
        schema={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://bgtechnochem.com/" },
            { "@type": "ListItem", "position": 2, "name": "About Us", "item": "https://bgtechnochem.com/about" }
          ]
        }}
      />
      
      {/* Header */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-4 bg-primary/10 text-primary hover:bg-primary/20 px-4 py-1 text-sm">Since 2000</Badge>
          <h1 className="text-4xl font-bold text-slate-900 mb-6">Welcome to BG TECHNO CHEM</h1>
          <p className="text-xl text-slate-600 max-w-4xl mx-auto leading-relaxed">
            {companyDetails.welcomeText}
          </p>
          <div className="mt-8 inline-flex items-center justify-center space-x-2 bg-white px-6 py-3 rounded-full shadow-sm border border-slate-200">
             <Award className="w-5 h-5 text-emerald-600" />
             <span className="font-semibold text-slate-800">{companyDetails.certifications}</span>
          </div>
        </div>
      </section>

      {/* Motto Section */}
      <section className="py-16 bg-primary text-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <Quote className="w-12 h-12 text-emerald-400 mx-auto mb-6 opacity-50" />
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4">
            OUR MOTTO
          </h2>
          <div className="flex flex-col md:flex-row justify-center items-center gap-4 md:gap-12 text-xl md:text-2xl font-light text-emerald-100 mt-8">
            <span className="flex items-center"><span className="w-2 h-2 bg-emerald-400 rounded-full mr-3"></span>SAVE WATER</span>
            <span className="flex items-center"><span className="w-2 h-2 bg-emerald-400 rounded-full mr-3"></span>SAVE ENERGY</span>
            <span className="flex items-center"><span className="w-2 h-2 bg-emerald-400 rounded-full mr-3"></span>SAVE MOTHER EARTH</span>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <Card className="bg-gradient-to-br from-blue-50 to-white border-none shadow-lg">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mb-6">
                  <Award className="text-white w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-primary">Our Strength</h3>
                <p className="text-slate-600 leading-relaxed text-lg">
                  Teams of extremely brilliant professionals are the true strength of our company who impart their duties with sheer candor and faithfulness. We are committed to providing environmental consultancy and solutions that matter.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-emerald-50 to-white border-none shadow-lg">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-emerald-600 rounded-lg flex items-center justify-center mb-6">
                  <Users className="text-white w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-emerald-800">Our Vision</h3>
                <p className="text-slate-600 leading-relaxed text-lg">
                  To be the global benchmark in industrial water engineering, recognized for technical excellence, customer-centricity, and environmental stewardship, making the environment absolutely free from pollutants.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src="https://images.unsplash.com/photo-1705147219565-fe9f6f369d03" 
                alt="Industrial Piping" 
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl font-bold mb-6">A Journey of Pure Dedication</h2>
              <div className="space-y-6 text-slate-600 text-lg">
                <p>
                  Founded in Ludhiana, Punjab in the year 2000, BG TECHNO CHEM started with a simple goal: to help local industries manage their wastewater effectively.
                </p>
                <p>
                  Over the decades, we have evolved into a comprehensive engineering firm. From designing small-scale ETPs to executing large municipal STPs, our journey is defined by continuous learning and technological adaptation.
                </p>
                <p>
                  Today, our team of expert engineers and chemists works tirelessly to deliver custom solutions that meet stringent pollution control norms and operational requirements.
                </p>
              </div>
              
              <div className="mt-8 grid grid-cols-2 gap-4">
                {[
                  "ISO 9001:2015 Certified",
                  "ISO 14001:2015 Certified",
                  "In-house R&D Lab",
                  "24/7 Support Team"
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center text-slate-800 font-medium">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mr-2" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
